import { datadogRum } from '@datadog/browser-rum'

let bootstrapPhase = 'startup'
let isClientErrorMonitoringInstalled = false

const isScriptElement = (target: EventTarget | null): target is HTMLScriptElement =>
  target instanceof HTMLScriptElement

const isLinkElement = (target: EventTarget | null): target is HTMLLinkElement =>
  target instanceof HTMLLinkElement

export const setBootstrapPhase = (phase: string): void => {
  bootstrapPhase = phase
}

export const getBootstrapPhase = (): string => bootstrapPhase

export const safeDatadogError = (
  message: string,
  context: Record<string, unknown>,
  error?: Error
): void => {
  try {
    datadogRum.addError(error ?? new Error(message), context)
  } catch {
    // Datadog may not be initialized yet; keep console diagnostics available.
  }
}

export const toSerializable = (value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: toSerializable((value as Error & { cause?: unknown }).cause),
    }
  }

  if (typeof value === 'object' && value !== null) {
    try {
      return structuredClone(value)
    } catch {
      try {
        return JSON.stringify(value)
      } catch {
        return '[unserializable object]'
      }
    }
  }

  return value
}

const onWindowError = (event: ErrorEvent): void => {
  const target = event.target
  const scriptElement = isScriptElement(target) ? target : null
  const linkElement = isLinkElement(target) ? target : null
  const isAssetError =
    (!!scriptElement || !!linkElement) && (!event.error || event.message === 'Script error.')

  const context = {
    phase: bootstrapPhase,
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    url: globalThis.location.href,
    isAssetError,
    assetSrc: scriptElement?.src,
    assetHref: linkElement?.href,
  }

  console.error('[global:error]', context, event.error)
  safeDatadogError('global_error', context, event.error instanceof Error ? event.error : undefined)
}

type PromiseRejectionEventLike = Event & { reason: unknown }

const onUnhandledRejection = (event: PromiseRejectionEventLike): void => {
  const reason = toSerializable(event.reason)
  const reasonText = typeof reason === 'string' ? reason : JSON.stringify(reason)
  const looksLikeChunkError =
    /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module/i.test(
      reasonText
    )

  const context = {
    phase: bootstrapPhase,
    reason,
    looksLikeChunkError,
    url: globalThis.location.href,
  }

  console.error('[global:unhandledrejection]', context)
  safeDatadogError(
    'unhandled_rejection',
    context,
    event.reason instanceof Error ? event.reason : undefined
  )
}

export const resetClientErrorMonitoringForTests = (): void => {
  isClientErrorMonitoringInstalled = false
  bootstrapPhase = 'startup'
}

export const installClientErrorMonitoring = (): void => {
  if (isClientErrorMonitoringInstalled) {
    return
  }

  isClientErrorMonitoringInstalled = true

  globalThis.addEventListener('error', onWindowError, true)
  globalThis.addEventListener('unhandledrejection', onUnhandledRejection)
}
